import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import DoctorAppointmentDetailModal from '../components/DoctorAppointmentDetailModal';
import AppointmentCalendar, { slotRange } from '../components/AppointmentCalendar';
import RoomCalendar from '../components/RoomCalendar';
import PatientDetailModal from '../components/PatientDetailModal';
import SearchBox, { appointmentPatientOptions, loadPatientOptions } from '../components/SearchBox';
import { vitalLines } from '../utils/patientVitals';
import { loadClinicLogo } from '../utils/clinicLogo';
import { downloadPrescriptionPdf } from '../utils/prescriptionPdf';

function visitStart(appointment) {
  const raw = new Date(appointment.APPOINTMENT_DATE);
  if (Number.isNaN(raw.getTime())) return null;
  const start = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  const range = slotRange(appointment.APPOINTMENT_SLOT);
  if (range) start.setMinutes(range.start);
  return start.getTime();
}

function formatVisitDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${date.getFullYear()}`;
}

function appointmentSearchText(appointment) {
  const patient = appointment.PATIENT_ID || {};
  return [
    patient.PATIENT_NAME,
    patient.PATIENT_ID,
    patient.PATIENT_PHONE,
    appointment.APPOINTMENT_PLACE,
    appointment.APPOINTMENT_STATUS,
    appointment.APPOINTMENT_TYPE,
    appointment.APPOINTMENT_SLOT,
    appointment.PATIENT_SYMPTOMS,
    appointment.DOCTOR_ID?.DOCTOR_NAME,
  ].filter(Boolean).join(' ').toLowerCase();
}

function splitAppointmentLists(appointments, query, now = Date.now()) {
  const text = query.trim().toLowerCase();
  const upcoming = [];
  const past = [];
  appointments.forEach((appointment) => {
    if (text && !appointmentSearchText(appointment).includes(text)) return;
    if (!text && appointment.APPOINTMENT_STATUS === 'Cancelled') return;
    const start = visitStart(appointment);
    if (start == null) return;
    if (start >= now) upcoming.push({ appointment, start });
    else past.push({ appointment, start });
  });
  upcoming.sort((left, right) => left.start - right.start);
  past.sort((left, right) => right.start - left.start);
  return {
    upcoming: upcoming.slice(0, 10).map((item) => item.appointment),
    past: past.slice(0, 5).map((item) => item.appointment),
  };
}

function appointmentListOptions(appointments) {
  const seen = new Set();
  const options = [];
  appointments.forEach((appointment) => {
    const patient = appointment.PATIENT_ID || {};
    const name = patient.PATIENT_NAME;
    if (!name || seen.has(name)) return;
    seen.add(name);
    options.push({
      key: patient._id || name,
      label: [name, patient.PATIENT_ID, patient.PATIENT_PHONE].filter(Boolean).join(' · '),
      item: name,
    });
  });
  return options.sort((left, right) => left.label.localeCompare(right.label));
}

function AppointmentListTable({ title, note, rows, onOpen }) {
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <h4 style={{ margin: '0 0 0.25rem 0', color: '#1f4d40' }}>{title}</h4>
      <p style={{ margin: '0 0 0.6rem 0', color: '#6c757d', fontSize: '0.85rem' }}>{note}</p>
      {rows.length === 0 ? (
        <p style={{ margin: 0, color: '#6c757d' }}>No appointments in this list.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                {['Patient', 'Date', 'Time', 'Place', 'Type', 'Status', ''].map((header) => (
                  <th key={header || 'open'} style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((appointment) => {
                const patient = appointment.PATIENT_ID || {};
                return (
                  <tr key={appointment._id || appointment.APPOINTMENT_ID}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>{patient.PATIENT_NAME || '—'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5', whiteSpace: 'nowrap' }}>{formatVisitDate(appointment.APPOINTMENT_DATE)}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5', whiteSpace: 'nowrap' }}>{appointment.APPOINTMENT_SLOT || '—'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>{appointment.APPOINTMENT_PLACE || '—'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>{appointment.APPOINTMENT_TYPE || '—'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>{appointment.APPOINTMENT_STATUS || '—'}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #f1f3f5' }}>
                      <button
                        type="button"
                        onClick={() => onOpen(appointment)}
                        style={{ padding: '4px 10px', backgroundColor: '#1f4d40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const DoctorDashboard = () => {
  // Active section state
  const [activeSection, setActiveSection] = useState(null);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // State for viewing patient details
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State for patient search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // State for Print Prescription
  const [prescriptionPatient, setPrescriptionPatient] = useState(null);
  const [prescriptionAppointment, setPrescriptionAppointment] = useState(null);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [appointmentPanel, setAppointmentPanel] = useState('calendar');
  const [appointmentListQuery, setAppointmentListQuery] = useState('');

  // Helper function to check if user has permission for a gadget
  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  // Function to fetch user permissions
  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setPermissionsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const permissions = await response.json();
        setUserPermissions(permissions);
        console.log('User permissions:', permissions);
      } else {
        console.error('Failed to fetch user permissions');
        // Set default permissions if fetch fails
        setUserPermissions({});
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Function to fetch appointments
  const fetchAppointments = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctors/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        const appointmentsData = Array.isArray(data) ? data : [];
        setAppointments(appointmentsData);
        setSelectedAppointment(null);
        
        // Debug: Log appointments data
        console.log('Fetched appointments:', appointmentsData);
        console.log('Completed appointments with diagnosis:', 
          appointmentsData.filter(apt => apt.APPOINTMENT_STATUS === 'Completed' && apt.DIAGNOSIS));
        
        // Show all appointment details
        appointmentsData.forEach((apt, index) => {
          console.log(`Appointment ${index + 1}:`, {
            id: apt._id,
            status: apt.APPOINTMENT_STATUS,
            diagnosis: apt.DIAGNOSIS,
            patient: apt.PATIENT_ID?.PATIENT_NAME,
            date: apt.APPOINTMENT_DATE
          });
        });
      } else {
        setError(data.message || 'Failed to fetch doctor appointments.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Patient search function
  const handleSearch = async (term) => {
    const query = typeof term === 'string' ? term : searchTerm;
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setSearchError('Authentication token not found. Please log in again.');
      setSearchLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/patients/search?search=${encodeURIComponent(query)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSearchResults(Array.isArray(data) ? data : []);
      } else {
        setSearchError(data.message || 'Failed to search patients.');
      }
    } catch (err) {
      setSearchError(`Network error: ${err.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewPatientDetails = (patient) => {
    setSelectedPatient(patient);
  };

  const handleClosePatientDetails = () => {
    setSelectedPatient(null);
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleCloseDetails = () => {
    setSelectedAppointment(null);
  };

  const handleSaveAppointment = () => {
    fetchAppointments();
  };

  // Filter appointments for prescription based on search term
  const getFilteredAppointments = () => {
    // TEMPORARY: Include all appointments for testing (remove DIAGNOSIS requirement)
    const completedAppointments = appointments.filter(apt => 
      apt.APPOINTMENT_STATUS === 'Completed' // Removed && apt.DIAGNOSIS for testing
    );

    console.log('All appointments:', appointments);
    console.log('Completed appointments with diagnosis:', completedAppointments);

    if (!appointmentSearchTerm.trim()) {
      return completedAppointments;
    }

    const searchLower = appointmentSearchTerm.toLowerCase();
    const filtered = completedAppointments.filter(appointment => {
      const patientName = appointment.PATIENT_ID?.PATIENT_NAME?.toLowerCase() || '';
      const patientId = appointment.PATIENT_ID?.PATIENT_ID?.toLowerCase() || '';
      const contactNumber = appointment.PATIENT_ID?.PATIENT_PHONE?.toLowerCase() || '';
      
      console.log('Searching for:', searchLower);
      console.log('Patient data:', {
        name: patientName,
        id: patientId,
        phone: contactNumber
      });
      
      return patientName.includes(searchLower) || 
             patientId.includes(searchLower) || 
             contactNumber.includes(searchLower);
    });
    
    console.log('Filtered results:', filtered);
    return filtered;
  };

  // Generate PDF prescription
  const generatePrescriptionPDF = async () => {
    if (!prescriptionAppointment) {
      alert('Please select an appointment to generate prescription.');
      return;
    }

    setPrescriptionLoading(true);
    
    try {
      const prescriptionLogo = await loadClinicLogo();
      await downloadPrescriptionPdf({ appointment: prescriptionAppointment, logo: prescriptionLogo });
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Error generating PDF: ${error.message}. Please check console for details.`);
    } finally {
      setPrescriptionLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
    fetchAppointments();
  }, []);

  // Trigger search when searchTrigger changes
  useEffect(() => {
    if (searchTrigger > 0) {
      console.log('Search triggered, appointments:', appointments);
      console.log('Search term:', appointmentSearchTerm);
      console.log('Filtered results:', getFilteredAppointments());
    }
  }, [searchTrigger, appointmentSearchTerm, appointments]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
      <h2>Doctor Dashboard</h2>
      
      {/* Dashboard Gadgets/Buttons */}
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          Loading dashboard permissions...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        
        {/* Patient Search Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🔍 Patient Search</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Search and view patient information
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'patientSearch' ? null : 'patientSearch')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'patientSearch' ? '#dc3545' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'patientSearch' ? 'Close Search' : 'Search Patients'}
            </button>
          </div>
        )}

        {/* Appointments Gadget */}
        {hasPermission('Appointment Scheduling') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📅 Appointments</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              View and manage scheduled appointments
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'appointments' ? null : 'appointments')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'appointments' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'appointments' ? 'Close Appointments' : 'View Appointments'}
            </button>
          </div>
        )}

        {hasPermission('Room Calendar') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🚪 Room calendar</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              See which room is booked and the room details
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'roomCalendar' ? null : 'roomCalendar')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'roomCalendar' ? '#dc3545' : '#0f766e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'roomCalendar' ? 'Close rooms' : 'View rooms'}
            </button>
          </div>
        )}

        {/* Print Prescription Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📄 Print Prescription</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Generate and print prescription PDFs
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'printPrescription' ? null : 'printPrescription')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'printPrescription' ? '#dc3545' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'printPrescription' ? 'Close Prescription' : 'Print Prescription'}
            </button>
          </div>
        )}
        
        {/* No gadgets available message */}
        {!permissionsLoading && !hasPermission('Patient Management') && !hasPermission('Appointment Scheduling') && !hasPermission('Room Calendar') && (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>
      )}

      {/* Conditional Sections */}
      {activeSection === 'patientSearch' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Patient Search</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            <SearchBox
              placeholder="Search patient by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              loadOptions={loadPatientOptions}
              onSelect={(option) => {
                const name = option.item.PATIENT_NAME || '';
                setSearchTerm(name);
                handleSearch(name);
              }}
            />
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {searchError && <p style={{ color: 'red', marginTop: '10px' }}>Error: {searchError}</p>}
          {searchLoading && <p>Searching for patients...</p>}

          {searchResults.length > 0 && (
            <div>
              <h4>Search Results:</h4>
              {searchResults.map((patient) => (
                <div key={patient.PATIENT_ID} style={{ border: '1px solid #eee', padding: '10px', marginBottom: '5px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{patient.PATIENT_NAME}</strong> - {patient.PATIENT_ID} - {patient.CONTACT_NUMBER}
                  </div>
                  <button
                    onClick={() => handleViewPatientDetails(patient)}
                    style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          )}
          {searchTerm && !searchLoading && searchResults.length === 0 && !searchError && (
            <p>No patients found matching your search.</p>
          )}
        </div>
      )}

      {activeSection === 'appointments' && hasPermission('Appointment Scheduling') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Scheduled Appointments</h3>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {['calendar', 'list'].map((panel) => (
                <button
                  key={panel}
                  type="button"
                  onClick={() => setAppointmentPanel(panel)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: '1px solid #007bff',
                    backgroundColor: appointmentPanel === panel ? '#007bff' : 'white',
                    color: appointmentPanel === panel ? 'white' : '#007bff',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {panel === 'calendar' ? 'Calendar' : 'List'}
                </button>
              ))}
            </div>
          </div>
          
          {loading && <p>Loading appointments...</p>}
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}

          {!loading && !error && appointmentPanel === 'calendar' && (
            <AppointmentCalendar appointments={appointments} onSelect={handleViewDetails} />
          )}

          {!loading && !error && appointmentPanel === 'list' && (
            <div>
              <SearchBox
                placeholder="Search by patient, phone, place, or status"
                value={appointmentListQuery}
                onChange={(event) => setAppointmentListQuery(event.target.value)}
                options={appointmentListOptions(appointments)}
                onSelect={(option) => setAppointmentListQuery(option.item)}
              />
              <AppointmentListTable
                title="Upcoming"
                note="Next 10 visits."
                rows={splitAppointmentLists(appointments, appointmentListQuery).upcoming}
                onOpen={handleViewDetails}
              />
              <AppointmentListTable
                title="Past"
                note="Last 5 visits."
                rows={splitAppointmentLists(appointments, appointmentListQuery).past}
                onOpen={handleViewDetails}
              />
            </div>
          )}
        </div>
      )}

      {/* Print Prescription Section */}
      {activeSection === 'roomCalendar' && hasPermission('Room Calendar') && (
        <RoomCalendar />
      )}

      {activeSection === 'printPrescription' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Print Prescription</h3>

          {/* Single Search Interface */}
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#495057', fontWeight: 'bold' }}>Search Patient Appointments</h4>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
              <SearchBox
                placeholder="Search patient by name, ID, or phone..."
                value={appointmentSearchTerm}
                onChange={(e) => setAppointmentSearchTerm(e.target.value)}
                options={appointmentPatientOptions(appointments)}
                onSelect={(option) => {
                  setAppointmentSearchTerm(option.item);
                  setSearchTrigger((prev) => prev + 1);
                }}
              />
              <button
                onClick={() => {
                  console.log('Search button clicked for:', appointmentSearchTerm);
                  console.log('Current appointments:', appointments);
                  console.log('Filtered appointments:', getFilteredAppointments());
                  setSearchTrigger(prev => prev + 1);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Search
              </button>
            </div>

            {/* Search Results */}
            {appointmentSearchTerm && (
              <div>
                <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                  Search Results ({getFilteredAppointments().length} found):
                </div>
                {getFilteredAppointments().length > 0 ? (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                    {getFilteredAppointments().map(appointment => (
                      <div
                        key={appointment.APPOINTMENT_ID}
                        onClick={() => setPrescriptionAppointment(appointment)}
                        style={{
                          padding: '0.75rem',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          backgroundColor: prescriptionAppointment?.APPOINTMENT_ID === appointment.APPOINTMENT_ID ? '#e3f2fd' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (prescriptionAppointment?.APPOINTMENT_ID !== appointment.APPOINTMENT_ID) {
                            e.target.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (prescriptionAppointment?.APPOINTMENT_ID !== appointment.APPOINTMENT_ID) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                          {appointment.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          ID: {appointment.PATIENT_ID?.PATIENT_ID} | 
                          Phone: {appointment.PATIENT_ID?.PATIENT_PHONE} | 
                          Date: {new Date(appointment.APPOINTMENT_DATE).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                          Status: {appointment.APPOINTMENT_STATUS} | 
                          Diagnosis: {appointment.DIAGNOSIS ? 'Yes' : 'No'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
                    No appointments found matching your search.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected Appointment Preview */}
          {prescriptionAppointment && (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '4px', border: '1px solid #c3e6c3' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontWeight: 'bold' }}>Selected Appointment:</h4>
              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Patient:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>ID:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_ID}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Phone:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_PHONE}
                  {vitalLines(prescriptionAppointment.PATIENT_ID?.VITALS).length > 0 && (
                    <span style={{ display: 'block' }}><strong>Vitals:</strong> {vitalLines(prescriptionAppointment.PATIENT_ID?.VITALS).join(' · ')}</span>
                  )}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Date:</strong> {new Date(prescriptionAppointment.APPOINTMENT_DATE).toLocaleDateString()}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Status:</strong> {prescriptionAppointment.APPOINTMENT_STATUS}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Diagnosis:</strong> {prescriptionAppointment.DIAGNOSIS || 'No diagnosis recorded'}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Medicines:</strong> {prescriptionAppointment.DR_MEDICINES?.length || 0} prescribed
                </div>
                <div>
                  <strong>Procedures:</strong> {prescriptionAppointment.DR_PROCEDURES?.length || 0} recommended
                </div>
              </div>
            </div>
          )}

          {/* Generate PDF Button */}
          <button
            onClick={generatePrescriptionPDF}
            disabled={prescriptionLoading || !prescriptionAppointment}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: prescriptionLoading || !prescriptionAppointment ? '#6c757d' : '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: prescriptionLoading || !prescriptionAppointment ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'background-color 0.3s ease'
            }}
          >
            {prescriptionLoading ? 'Generating PDF...' : 'Generate & Download Prescription PDF'}
          </button>

          {!prescriptionAppointment && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6c757d' }}>
              Please select a completed appointment with diagnosis to generate prescription.
            </p>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedAppointment && (
        <DoctorAppointmentDetailModal
          appointment={selectedAppointment}
          onClose={handleCloseDetails}
          onSave={handleSaveAppointment}
        />
      )}

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={handleClosePatientDetails}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;